import React from 'react';

import { PlusOutlined } from '@ant-design/icons';
import { Button, Flex, Select, SelectProps, Typography } from 'antd';

import { useTranslation } from 'react-i18next';

import { NSKeys } from '@app/i18n';
import { RetrieveContactSchema } from '@app/models/dictionaries';

import { Create } from './components/Create';
import { useContactsQuery, useGetContact } from './hooks';

interface IProps extends SelectProps {
  placeholder: string;
  group_uid_ne?: string;
  disableValues?: string[];
  creatable?: boolean;
  recipientUid?: string;
  onAddContact?: (values: RetrieveContactSchema) => void;
}

interface IOption {
  label: string;
  value: string;
  data: any;
  disabled: boolean;
}

export const ContactsSelect: React.FC<IProps> = ({
  placeholder,
  group_uid_ne,
  disableValues = [],
  creatable = false,
  onAddContact,
  ...props
}) => {
  const commonT = useTranslation(NSKeys.common);
  const [search, setSearch] = React.useState('');
  const [initialValue, setInitialValue] = React.useState(props.value);
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const [options, setOptions] = React.useState<IOption[]>([]);
  const nomenclatureCartT = useTranslation(NSKeys.nomenclatureCart);

  const { data: initialContact } = useGetContact(initialValue);

  const { data, hasNextPage, fetchNextPage, isFetching } = useContactsQuery({
    limit: 10,
    search: search,
    recipient_uid: props.recipientUid,
  });

  React.useEffect(() => {
    if (data) {
      setOptions(
        data?.pages
          ?.map((data) => data.items || [])
          .flat()
          ?.map((el) => ({
            data: el,
            label: el.name,
            value: el.uid,
            disabled: disableValues.some((disableEl) => disableEl === el.uid),
          }))
      );
    } else if (initialContact) {
      setOptions([
        {
          data: initialContact,
          label: initialContact.name,
          value: initialContact.uid,
          disabled: disableValues.some((disableEl) => disableEl === initialContact.uid),
        },
      ]);
    }
  }, [data, initialContact]);

  const handleAdd = (data: RetrieveContactSchema) => {
    setIsModalOpen(false);
    onAddContact?.(data);
    setOptions([
      {
        data: data,
        label: data.name,
        value: data.uid,
        disabled: false,
      },
    ]);
  };

  const handleCancel = () => {
    setIsModalOpen(false);
  };

  const renderAddButton = () => (
    <Button
      type="text"
      icon={<PlusOutlined />}
      onClick={() => setIsModalOpen(true)}
      size="small"
      style={{ padding: 12 }}>
      <Flex gap={12}>
        <Typography.Text type="secondary" style={{ margin: 0, fontSize: 12 }}>
          {commonT.t('buttonAdd')}:
        </Typography.Text>
        <Typography.Text style={{ margin: 0, fontSize: 12 }}>{search}</Typography.Text>
      </Flex>
    </Button>
  );

  return (
    <>
      <Select
        allowClear
        onPopupScroll={(e: any) => {
          const { target } = e;
          if (target?.scrollTop + 10 + target?.offsetHeight > target?.scrollHeight && hasNextPage && !isFetching) {
            fetchNextPage();
          }
        }}
        onSearch={setSearch}
        filterOption={false}
        options={options}
        // notFoundContent={isFetching ? <Spin size="small" /> : null}
        popupRender={(menu) => (
          <div>
            {menu}
            <>{creatable && search ? renderAddButton() : null}</>
          </div>
        )}
        optionRender={(option) => (
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <Typography.Text role="img" style={{ margin: 0 }}>
              {option.label}
            </Typography.Text>
            <Typography.Text type="secondary" style={{ margin: 0, fontSize: 12 }}>
              {nomenclatureCartT.t('orderForm.companyContactPhone')}:&nbsp;{option.data?.data?.phone}
            </Typography.Text>
          </div>
        )}
        {...props}
      />
      {isModalOpen && (
        <Create onAdd={handleAdd} onCancel={handleCancel} isModalOpen={isModalOpen} initialValue={search} />
      )}
    </>
  );
};
